/**
 * Rules Engine hooks (FND-007, H1 — deterministic + explainable rules).
 *
 * Wraps the read-only rule-set surface plus the dry-run simulation:
 *   - GET  /api/v1/rules                       → list tenant-scoped rule sets
 *   - POST /api/v1/rules/{id}/simulate         → dry-run a version (no audit log)
 *
 * Simulation is non-mutating: it returns an explainable {@link EvaluationResult}
 * (decision + factor breakdown) without writing a `rule_eval_log`, so the admin
 * console can preview how a (possibly draft) version would behave.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** The decision domain a rule set governs (TECH_SPEC §7, H1 rule families). */
export type DecisionType = 'prioritization' | 'effective_date' | 'state_routing' | string;

/** A configurable decision domain for one tenant (read-only view). */
export interface RuleSet {
  rule_set_id: string;
  tenant_id: string;
  name: string;
  decision_type: DecisionType;
  active_version: number;
  description?: string | null;
}

/** A single explainable contributing factor in an evaluation. */
export interface Factor {
  name: string;
  label: string;
  value: number | string;
  weight: number;
  contribution: number;
  detail?: string | null;
}

/** Explainable output of a rule evaluation/simulation. */
export interface EvaluationResult {
  rule_set_id: string;
  decision_type: DecisionType;
  /** The output, e.g. `{ priority_score: 0.82 }` or `{ route: 'TX' }`. */
  decision: Record<string, unknown>;
  factor_breakdown: Factor[];
  rule_version: number;
  explanation?: string | null;
}

/** Body for a simulation: input facts plus the explicit version to dry-run. */
export interface SimulateRequest {
  context: Record<string, unknown>;
  version: number;
}

interface RuleSetListResponse {
  items: RuleSet[];
}

interface UseRuleSetsResult {
  ruleSets: RuleSet[];
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the tenant-scoped rule-set catalog (read-only). */
export function useRuleSets(): UseRuleSetsResult {
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<RuleSetListResponse>('/rules')
      .then((res) => {
        setRuleSets(res.items);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setRuleSets([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ruleSets, isLoading, error, refresh };
}

interface UseRuleSimulationResult {
  result: EvaluationResult | null;
  isSimulating: boolean;
  error: ApiError | null;
  /** Dry-run a rule version against the supplied input facts. */
  simulate: (ruleSetId: string, req: SimulateRequest) => Promise<EvaluationResult | null>;
  clear: () => void;
}

/** Non-mutating 'simulate rule' panel: posts to /rules/{id}/simulate. */
export function useRuleSimulation(): UseRuleSimulationResult {
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const simulate = useCallback(
    async (ruleSetId: string, req: SimulateRequest): Promise<EvaluationResult | null> => {
      setIsSimulating(true);
      setError(null);
      try {
        const res = await apiClient.post<EvaluationResult>(
          `/rules/${encodeURIComponent(ruleSetId)}/simulate`,
          req
        );
        setResult(res);
        return res;
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setResult(null);
        return null;
      } finally {
        setIsSimulating(false);
      }
    },
    []
  );

  const clear = useCallback(() => {
    setResult(null);
    setError(null);
    setIsSimulating(false);
  }, []);

  return { result, isSimulating, error, simulate, clear };
}
